import type { RoomId } from '@engineer-os/chat';
import { clockTime, dayLabelWithYear } from '../../lib/format';
import { useStrings } from '../../state/useLocale';
import { Parts } from '../../ui';
import type { ChatApi } from '../../state/useChat';
import { RichText } from './CustomEmoji';

/**
 * What was said to you while you were not looking.
 *
 * Everything else in this panel is a socket: close the tab and it is gone,
 * and a room you were not in when somebody answered you is a room where that
 * answer is now a thousand messages back. This is the one part of the chat
 * that outlives the tab, and it is deliberately the smallest possible version
 * of that idea — mentions, newest first, each one a way back to where it was
 * said. Not a feed, not a notification centre, not a second unread system.
 *
 * It is kept with the account rather than with the browser, which is the whole
 * reason it can exist: a tag on an account is the same person on every device,
 * so the row the Worker wrote when somebody typed your name is findable from
 * whichever machine you next sign in on. A browser with no account has nowhere
 * to put one, and this says so in a sentence rather than showing an empty list
 * that looks broken.
 *
 * Each row is a button and nothing else. Opening one goes to the message —
 * switching room if that room is open, paging to it if it is not on screen —
 * and marks that one seen on the way past, because reading it is the whole of
 * what "seen" means and asking somebody to also press a button about it is
 * asking them to do the system's bookkeeping.
 *
 * "Unread elsewhere" is a count and not a list, on purpose. It is the rooms
 * this tab is *not* holding a socket to, which means the tab cannot say what
 * is in them without opening them, and opening ninety-nine rooms to count
 * their messages is exactly the fan-out the whole presence design exists to
 * avoid. A number the account already knows is honest; a list would be a
 * promise this cannot keep.
 */

/** Reached through `ChatApi` so the hook can keep the declaration wherever it lives. */
type InboxState = ChatApi['inbox'];

export interface InboxProps {
  inbox: InboxState;
  /** False while signed out, when there is nothing to hold a mention. */
  signedIn: boolean;
  /** Go to one: the room, and the message in it. */
  onOpen(room: RoomId, messageId: number): void;
  /** Acknowledge some, or all of them. */
  onSeen(ids?: number[]): void;
  /** Whether a mention may raise a browser notification on this browser. */
  notifyMentions: boolean;
  /**
   * Turn it on or off. It has to be called from inside the click that turned
   * it on: asking a browser for notification permission outside a gesture is
   * refused, and in some browsers it throws.
   */
  onNotify(on: boolean): void;
}

export function Inbox({ inbox, signedIn, onOpen, onSeen, notifyMentions, onNotify }: InboxProps) {
  const s = useStrings().chat;

  if (!signedIn) {
    return (
      <div className="chat-inbox">
        <p className="note">{s.inbox.signedOut}</p>
      </div>
    );
  }

  return (
    <div className="chat-inbox">
      {inbox.loading && inbox.mentions.length === 0 && <p className="chat-empty">{s.inbox.loading}</p>}
      {!inbox.loading && inbox.mentions.length === 0 && <p className="chat-empty">{s.inbox.empty}</p>}

      {inbox.mentions.length > 0 && (
        <ul className="chat-inbox-list" aria-label={s.inbox.label}>
          {inbox.mentions.map((m) => {
            const when = new Date(m.ts);
            return (
              <li key={m.id} className="chat-inbox-item">
                <button
                  type="button"
                  className="chat-inbox-open"
                  title={s.inbox.go}
                  onClick={() => {
                    // Reading it is what "seen" means. Nobody should have to
                    // tell the system twice.
                    onSeen([m.id]);
                    onOpen(m.room, m.messageId);
                  }}
                >
                  <span className="label">
                    <Parts
                      of={s.inbox.from(
                        <span key="name" dir="auto">
                          {m.fromName}
                        </span>,
                        m.room,
                      )}
                    />
                  </span>
                  {/* The clock is the machine's and reads left to right in
                      both languages; the line is the writer's and takes its
                      own direction (docs/i18n.md §7). */}
                  <time className="msg-time" dir="ltr" dateTime={when.toISOString()} title={dayLabelWithYear(when)}>
                    {clockTime(when)}
                  </time>
                  <span className="msg-text" dir="auto">
                    <RichText text={m.text} />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="chat-inbox-foot">
        {inbox.elsewhere > 0 && <span className="note">{s.inbox.elsewhere(inbox.elsewhere)}</span>}
        {inbox.mentions.length > 0 && (
          <button type="button" className="chat-link" onClick={() => onSeen()}>
            {s.inbox.markAll}
          </button>
        )}
        {/* A tag with a pressed state rather than a checkbox: there is not a
            checkbox anywhere else in this product, and the tag is the shape
            the design system already has for a thing that is on or off.
            Pressing it is what asks the browser for permission, which is why
            it is a button and not a stored preference somebody else writes —
            the ask only works inside the gesture that caused it. */}
        <button
          type="button"
          className={notifyMentions ? 'tag tag-on chat-inbox-notify' : 'tag chat-inbox-notify'}
          aria-pressed={notifyMentions}
          title={s.inbox.notifyWhy}
          onClick={() => onNotify(!notifyMentions)}
        >
          {s.inbox.notify}
        </button>
      </div>
    </div>
  );
}

export default Inbox;
